import { useEffect, useState } from 'react'
import { IconoVolver, IconoWhatsapp } from '../../componentes/iconos'
import { enlaceWaMe } from '../../componentes/PerfilCliente'
import type { Canal } from './consultas'
import {
  ETAPAS,
  ETIQUETA_ETAPA,
  etapaDesdeStage,
  useLead,
  useGuardarFicha,
  useGuardarNota,
  useMoverEtapa,
  type Etapa,
} from './lead'

interface Props {
  canal: Canal
  telefono: string | null
  nombre: string
  onCerrar: () => void
  /** En la columna fija (3-col) no hay cabecera de cajón ni botón cerrar. */
  sinCabecera?: boolean
}

/**
 * Panel flotante de lead (estilo Vocero): etapa del pipeline, ficha de
 * cualificación y notas. Todo lo que se edita aquí se propaga al crm.lead de
 * Odoo por la doble conexión del backend. Si no hay teléfono enlazable (p. ej.
 * una sesión de cliente sin número), muestra el aviso en vez de los controles.
 */
export function PanelLead({ canal, telefono, nombre, onCerrar, sinCabecera = false }: Props) {
  const { data, isPending } = useLead(canal, telefono)
  const lead = data?.lead ?? null
  const guardarFicha = useGuardarFicha(canal)
  const guardarNota = useGuardarNota(canal)
  const moverEtapa = useMoverEtapa(canal)

  const etapaActiva = etapaDesdeStage(lead?.stageId ?? null)
  const [nota, setNota] = useState('')
  const [editandoFicha, setEditandoFicha] = useState(false)
  const [ficha, setFicha] = useState({ zip: '', email: '', city: '', street: '' })

  useEffect(() => {
    if (lead) setFicha({ zip: lead.codigoPostal, email: lead.email, city: lead.ciudad, street: lead.calle })
  }, [lead])

  const sinTelefono = !telefono

  function onEtapa(etapa: Etapa) {
    if (!telefono) return
    moverEtapa.mutate({ telefono, etapa })
  }
  function onGuardarFicha() {
    if (!telefono) return
    guardarFicha.mutate(
      { telefono, contactName: nombre, zip: ficha.zip, email: ficha.email, city: ficha.city, street: ficha.street },
      { onSuccess: () => setEditandoFicha(false) },
    )
  }
  function onGuardarNota() {
    if (!telefono || !nota.trim()) return
    guardarNota.mutate({ telefono, nota: nota.trim() }, { onSuccess: () => setNota('') })
  }

  return (
    <aside
      aria-label={`Detalles del lead ${nombre}`}
      className="flex h-full w-full flex-col overflow-hidden bg-superficie"
    >
      {sinCabecera ? null : (
        <header className="flex items-center gap-2 border-b border-borde px-4 py-3">
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar panel"
            className="-ml-1 flex size-8 items-center justify-center rounded-lg text-tinta-apagada transition-colors hover:bg-elevado"
          >
            <IconoVolver aria-hidden="true" className="size-5" />
          </button>
          <h2 className="text-xs font-bold uppercase tracking-wider text-tinta-apagada">Detalles</h2>
        </header>
      )}

      <div className={`flex flex-col gap-5 overflow-y-auto p-4 ${sinCabecera ? 'pr-14' : ''}`}>
        {/* Persona */}
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-full bg-acento text-sm font-bold text-white">
            {nombre.slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-tinta">{nombre}</p>
            <p className="text-xs text-tinta-apagada">{telefono ?? 'Sin teléfono enlazado'}</p>
          </div>
        </div>

        {enlaceWaMe(telefono) ? (
          <a
            href={enlaceWaMe(telefono) as string}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado"
          >
            <IconoWhatsapp aria-hidden="true" className="size-4" />
            Ver cliente en WhatsApp
          </a>
        ) : null}

        {sinTelefono ? (
          <p className="rounded-lg border border-borde bg-elevado px-3 py-2 text-xs text-tinta-apagada">
            Esta conversación no tiene un teléfono enlazable, así que no se puede sincronizar con el
            lead de Odoo. Pasa por una conversación de leads con número.
          </p>
        ) : (
          <>
            {/* Etapa del pipeline */}
            <section>
              <p className="mb-2.5 text-xs font-bold uppercase tracking-wider text-tinta-apagada">
                Etapa del pipeline
              </p>
              <ol className="flex flex-col gap-2.5">
                {ETAPAS.map((etapa) => {
                  const idx = ETAPAS.indexOf(etapa)
                  const activaIdx = etapaActiva ? ETAPAS.indexOf(etapaActiva) : -1
                  const hecha = activaIdx >= 0 && idx < activaIdx
                  const actual = etapa === etapaActiva
                  return (
                    <li key={etapa}>
                      <button
                        type="button"
                        onClick={() => onEtapa(etapa)}
                        disabled={moverEtapa.isPending}
                        className={`flex w-full items-center gap-2.5 text-left text-sm transition-colors disabled:opacity-60 ${
                          actual ? 'font-bold text-acento' : 'text-tinta hover:text-acento'
                        }`}
                      >
                        <span
                          className={`flex size-4 flex-none items-center justify-center rounded-full border-2 ${
                            hecha || actual ? 'border-acento bg-acento' : 'border-borde'
                          }`}
                        >
                          {hecha ? <span className="text-[10px] font-black text-white">✓</span> : null}
                        </span>
                        {ETIQUETA_ETAPA[etapa]}
                      </button>
                    </li>
                  )
                })}
              </ol>
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-acento-borde bg-acento-suave px-2 py-0.5 text-[10px] font-bold text-acento">
                ⟳ sincroniza con Odoo crm.lead
              </p>
            </section>

            {/* Ficha */}
            <section className="border-t border-borde-suave pt-3.5">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-tinta-apagada">Ficha</p>
                <button
                  type="button"
                  onClick={() => setEditandoFicha((v) => !v)}
                  className="text-xs text-acento hover:underline"
                >
                  {editandoFicha ? 'Cancelar' : '+ Editar'}
                </button>
              </div>
              {isPending ? (
                <p className="text-xs text-tinta-apagada">Cargando ficha…</p>
              ) : editandoFicha ? (
                <div className="flex flex-col gap-2">
                  {(['zip', 'email', 'city', 'street'] as const).map((campo) => (
                    <input
                      key={campo}
                      value={ficha[campo]}
                      onChange={(e) => setFicha((f) => ({ ...f, [campo]: e.target.value }))}
                      placeholder={
                        campo === 'zip' ? 'Código postal' : campo === 'email' ? 'Correo' : campo === 'city' ? 'Población' : 'Dirección'
                      }
                      className="rounded-lg border border-borde bg-superficie px-3 py-2 text-sm text-tinta outline-none focus:border-acento"
                    />
                  ))}
                  <button
                    type="button"
                    onClick={onGuardarFicha}
                    disabled={guardarFicha.isPending}
                    className="mt-1 rounded-lg bg-acento px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  >
                    {guardarFicha.isPending ? 'Guardando…' : 'Guardar ficha'}
                  </button>
                </div>
              ) : (
                <dl className="flex flex-col gap-1.5 text-[13px]">
                  <FilaFicha k="Código postal" v={lead?.codigoPostal} />
                  <FilaFicha k="Correo" v={lead?.email} />
                  <FilaFicha k="Población" v={lead?.ciudad} />
                  <FilaFicha k="Etapa Odoo" v={lead?.stageNombre} />
                  {lead?.ingresoEsperado ? <FilaFicha k="Ingreso esperado" v={`${lead.ingresoEsperado} €`} /> : null}
                </dl>
              )}
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-acento-borde bg-acento-suave px-2 py-0.5 text-[10px] font-bold text-acento">
                ⟳ guardar ficha → Odoo crm.lead
              </p>
            </section>

            {/* Notas */}
            <section className="border-t border-borde-suave pt-3.5">
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-tinta-apagada">Notas</p>
              <textarea
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                placeholder="Escribe una nota; queda en el chatter del lead en Odoo…"
                className="min-h-[70px] w-full resize-y rounded-lg border border-borde bg-superficie p-2.5 text-sm text-tinta outline-none focus:border-acento"
              />
              <button
                type="button"
                onClick={onGuardarNota}
                disabled={guardarNota.isPending || !nota.trim()}
                className="mt-2 rounded-lg bg-acento px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {guardarNota.isPending ? 'Guardando…' : 'Guardar nota'}
              </button>
              {guardarNota.isSuccess ? (
                <p className="mt-1.5 text-xs text-positivo-tinta">Nota enviada a Odoo ✓</p>
              ) : null}
            </section>
          </>
        )}
      </div>
    </aside>
  )
}

function FilaFicha({ k, v }: { k: string; v?: string | null }) {
  return (
    <div className="flex justify-between gap-2 border-b border-dashed border-borde-suave py-1">
      <dt className="font-medium text-tinta">{k}</dt>
      <dd className="truncate text-tinta-apagada">{v || '—'}</dd>
    </div>
  )
}
